import { PlanType, PlanSNPType } from "../../../@types";
import messages_en from "../../../translations/en-US.json";

export enum SearchFilterKeys {
  drugCoverageFilter = "drugCoverageFilter",
  insuranceCarrierFilter = "insuranceCarrierFilter",
  planCategoriesFilter = "planCategoriesFilter",
  planCoveragesFilter = "planCoveragesFilter",
  starRatingFilter = "starRatingFilter",
  specialNeedsFilter = "specialNeedsFilter",
}

export const SearchFilterKeyMapping: Record<SearchFilterKeys, string> = {
  [SearchFilterKeys.planCoveragesFilter]:
    messages_en["search_results_page.filters.plan_with_these_benefits"],
  [SearchFilterKeys.insuranceCarrierFilter]:
    messages_en["plan_terms.insurance_carrier"],
  [SearchFilterKeys.planCategoriesFilter]:
    messages_en["search_results_page.filters.types_of_plans"],
  [SearchFilterKeys.drugCoverageFilter]:
    messages_en["search_results_page.drug_coverage_options"],
  [SearchFilterKeys.starRatingFilter]:
    messages_en["plan_terms.star_rating_filter"],
  [SearchFilterKeys.specialNeedsFilter]:
    messages_en["search_results_page.add_special_needs_plans"],
};

/**
 * A mapping of API-friendly coverage filter names, used in conjunction with [[filterLabels]] below as part of the [[PlanCoveragesFilter]] component.
 */
const filters = {
  withDental: "dental_coverage",
  withHearing: "hearing_coverage",
  withVision: "vision_coverage",
  allMyDrugs: "all_covered_drugs",
  transportation: "transportation",
  fitnessBenefits: "silver_sneakers",
};

const {
  withDental,
  withHearing,
  withVision,
  //allMyDrugs,
  transportation,
  fitnessBenefits,
} = filters;

/**
 * Setup filters for each plan type using [[filters]]
 */
const filtersByPlan = {
  [PlanType.MA]: {
    withVision,
    withDental,
    withHearing,
    transportation,
    fitnessBenefits,
  },
  [PlanType.MAPD]: {
    withVision,
    withDental,
    withHearing,
    transportation,
    fitnessBenefits,
  },
  [PlanType.MEDIGAP]: {},
};

/**
 * A mapping of human-friendly filter labels corresponding to [[filters]] above, used primarily in the [[PlanCoveragesFilter]] component.
 */
const filterLabels = {
  withVision: "filter_labels.with_vision",
  transportation: "filter_labels.transportation",
  withDental: "filter_labels.with_dental",
  fitnessBenefits: "filter_labels.fitness_benefits",
  withHearing: "filter_labels.with_hearing",
  allMyDrugs: "filter_labels.all_drugs",
};

/**
 * A mapping of human-friendly filter labels corresponding to [[filters]] above, used primarily in the [[PlanFiltersContainer]] component.
 */
const filterLabelsByValue = {
  [filters.withDental]: filterLabels.withDental,
  [filters.withHearing]: filterLabels.withHearing,
  [filters.withVision]: filterLabels.withVision,
  [filters.allMyDrugs]: filterLabels.allMyDrugs,
  [filters.transportation]: filterLabels.transportation,
  [filters.fitnessBenefits]: filterLabels.fitnessBenefits,
};

const TealiumConversions = {
  [filters.allMyDrugs]: "Has All my Drugs on Formulary",
  [filters.fitnessBenefits]: "Fitness Benefits",
  [filters.transportation]: "Transportation",
  [filters.withDental]: "Dental Coverage",
  [filters.withHearing]: "Hearing Coverage",
  [filters.withVision]: "Vision Coverage",
};

const categoryFilterMap = {
  hmo: "PLAN_FILTER_HMO",
  ppo: "PLAN_FILTER_PPO",
  pffs: "PLAN_FILTER_PFFS",
  msa: "PLAN_FILTER_MSA",
  cost: "PLAN_FILTER_COST",
};

const categoryFilterTranslationMap = {
  PLAN_FILTER_HMO: "search_results_page.filter_labels.hmo",
  PLAN_FILTER_PPO: "search_results_page.filter_labels.ppo",
  PLAN_FILTER_PFFS: "search_results_page.filter_labels.pffs",
  PLAN_FILTER_MSA: "search_results_page.filter_labels.msa",
  PLAN_FILTER_COST: "search_results_page.filter_labels.cost",
};

const snpFilterTranslationMap: Record<
  Exclude<PlanSNPType, PlanSNPType.SNP_TYPE_NOT_SNP>,
  string
> = {
  [PlanSNPType.SNP_TYPE_CHRONIC_OR_DISABLING]:
    "search_results_page.plans_for_disabling_conditions",
  [PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE]:
    "search_results_page.plans_for_both_medicare_and_medicaid",
  [PlanSNPType.SNP_TYPE_INSTITUTIONAL]:
    "search_results_page.plans_for_long_term_care_facilities",
};

export default {
  categoryFilterMap,
  categoryFilterTranslationMap,
  snpFilterTranslationMap,
  filtersByPlan,
  filterLabelsByValue,
  filterLabels,
  TealiumConversions,
  SearchFilterKeys,
};
